<template>
  <div>
    <NuxtLoadingIndicator color="#ff6a00" />
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
<script setup lang="ts">
  import { registerGioPlugins } from './utils/gio'
  import { ensureHomeSdkRouteHook, isHomeRoute } from './utils/homeRoute'
  import zhData from '../i18n/locales/lang/zh-CN.js' // 引入 JS 文件

  const { mergeLocaleMessage } = useI18n({ useScope: 'global' })

  // 1. 获取基础内容
  let rawMessages = zhData.default || zhData

  // 2. ✨ 核心防弹逻辑：处理不同的导出类型
  const initZhMessages = async () => {
    try {
      // 情况 A：如果它是一个函数 (比如用了 defineI18nLocale)，先执行它
      if (typeof rawMessages === 'function') {
        rawMessages = await rawMessages()
      }

      // 情况 B：如果执行完毕后，里面还套着 default，再剥一次
      const finalData = rawMessages?.default || rawMessages

      if (!finalData) {
        console.error('⚠️ 中文包为空，请检查 zh-CN.js 的 export 语法是否正确')
        return
      }

      // 3. 执行洗数据和合并
      const pureZhMessages = JSON.parse(JSON.stringify(finalData))
      mergeLocaleMessage('zh-CN', pureZhMessages)
    } catch (error) {
      console.error('⚠️ 中文包解析或转换失败：', error)
    }
  }

  // 立即触发初始化
  initZhMessages()
  const { public: PublicConfig } = useRuntimeConfig()

  const { isCn } = useVipLogo()
  const route = useRoute()
  const router = useRouter()
  const prodUrl = `cloudapi.jctrans.com`
  const isProd = computed(() =>
    PublicConfig.PROD_CLIENT_PROXY_API.includes(prodUrl),
  )
  // SEM 页依赖巨量 _baq 即时可用，埋点保持同步注入；其它页再延后
  const isSemPage = computed(() => (route.path || '').includes('/sem'))
  const isHomePage = computed(() => isHomeRoute(route))

  const DOUYIN_SCRIPT = `(function(r,d,s){r._tt_config=true;var _baq=(r._baq=r._baq||[]);_baq.methods=["track","off","on"];
_baq.factory=function(method){return function(){var args=Array.prototype.slice.call(arguments);
args.unshift(method);_baq.push(args);return _baq}};for(var i=0;i<_baq.methods.length;i++){
var key=_baq.methods[i];_baq[key]=_baq.factory(key)}_baq.load=function(){
var js,fjs=d.getElementsByTagName(s)[0];js=d.createElement(s);
js.src="https://analytics.oceanengine.com/api/v2/js/sdk";fjs.parentNode.insertBefore(js,fjs)};
_baq.load();if(_baq.invoked){return}_baq.invoked=true;_baq.track("pageview")})(window,document,"script");`

  const BAIDU_SCRIPT = `var _hmt = _hmt || [];
(function() {
  var hm = document.createElement("script");
  hm.src = "https://hm.baidu.com/hm.js?23b9304a8a6b578f453b31db081c074a";
  var s = document.getElementsByTagName("script")[0];
  s.parentNode.insertBefore(hm, s);
})();`

  const GTAG_INIT = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-V2CW8DTKQC');
`

  // tracking.js / gtag 已加载后挂表单转化；避免双载时仍可独立工作
  const GTAG_FORM_TRACK = `
window.addEventListener('load', function () {
  const submitBtn = document.querySelector('#register-form button[type="submit"]');
  if (!submitBtn) return;
  submitBtn.addEventListener('click', function () {
    const form = submitBtn.closest('form');
    if (!form) return;
    const companyInput = form.querySelector('input[placeholder*="Company Name"]');
    const emailInput = form.querySelector('input[placeholder*="Business Email"]');
    const locationInput = form.querySelector('input[placeholder*="Location"]');
    const industryInput = form.querySelector('input[name="industrySector"]');
    const agreementCheck = form.querySelector('.van-checkbox');

    const company = companyInput ? companyInput.value.trim() : "";
    const email = emailInput ? emailInput.value.trim().toLowerCase() : "";
    const location = locationInput ? locationInput.value.trim() : "";
    const industry = industryInput ? industryInput.value.trim() : "";

    const hasErrors = form.querySelectorAll('.van-field__error-message').length > 0;
    const isAgreed = agreementCheck && agreementCheck.getAttribute('aria-checked') === 'true';

    if (company !== "" && email !== "" && location !== "" && industry !== "" && !hasErrors && typeof gtag === 'function') {
      gtag('set', 'user_data', { "email": email });
      gtag('event', 'conversion', { 'send_to': 'AW-18007422292/JQh7CKvnio8cENTqzYpD' });
      gtag('event', 'submit', { 'send_to': 'G-V2CW8DTKQC' });
    }
  });
});
`

  const trackingSrc = computed(() =>
    isProd.value
      ? 'https://resources.jctrans.com/res/eratracking/h5/static/js/tracking.js'
      : 'https://resources.jctrans.com/res/eratracking/h5/static/js/tracking-dev.js',
  )

  let gioPluginsReady = false
  let businessTrackingReady = false

  /** 非 SEM：空闲时注入，脚本内容与原先一致，只改加载时机 */
  const scheduleIdle = (fn: () => void, timeout = 1500) => {
    if (typeof window === 'undefined') return
    const run = () => {
      try {
        fn()
      } catch {
        // 埋点失败不影响业务
      }
    }
    const ric = (
      window as Window & {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number
      }
    ).requestIdleCallback
    if (typeof ric === 'function') {
      ric(run, { timeout })
      return
    }
    if (document.readyState === 'complete') {
      setTimeout(run, 1)
      return
    }
    window.addEventListener('load', () => setTimeout(run, 1), { once: true })
  }

  const injectInlineScript = (key: string, code: string) => {
    if (document.querySelector(`script[data-defer-track="${key}"]`)) return
    const el = document.createElement('script')
    el.type = 'text/javascript'
    el.dataset.deferTrack = key
    el.text = code
    document.head.appendChild(el)
  }

  const injectExternalScript = (key: string, src: string, async = true) => {
    if (document.querySelector(`script[data-defer-track="${key}"]`)) return
    const el = document.createElement('script')
    el.type = 'text/javascript'
    el.async = async
    el.dataset.deferTrack = key
    el.src = src
    document.head.appendChild(el)
  }

  const loadDeferredTrackers = () => {
    injectInlineScript('douyin', DOUYIN_SCRIPT)
    injectInlineScript('baidu', BAIDU_SCRIPT)
  }

  const ensureGioPlugins = () => {
    if (gioPluginsReady) return
    registerGioPlugins()
    gioPluginsReady = true
  }

  /** 业务 tracking：非首页跟 load；首页 idle，离开首页立刻补齐 */
  const loadBusinessTracking = () => {
    if (businessTrackingReady) return
    injectExternalScript('tracking', trackingSrc.value)
    businessTrackingReady = true
  }

  const scheduleBusinessTrackingOnLoad = () => {
    if (document.readyState === 'complete') {
      loadBusinessTracking()
      return
    }
    window.addEventListener('load', loadBusinessTracking, { once: true })
  }

  /** 英文站：gtag 基础库 + 表单转化；延后注入避免挡首页 FCP */
  const loadEnGtag = () => {
    if (isCn.value) return
    injectExternalScript(
      'gtag-js',
      'https://www.googletagmanager.com/gtag/js?id=G-V2CW8DTKQC',
    )
    injectInlineScript('gtag-init', GTAG_INIT)
    injectInlineScript('gtag-form-track', GTAG_FORM_TRACK)
  }

  /** 离开首页时立刻补齐，避免进 /company/ 丢埋点 */
  const ensureHomeDeferredReady = () => {
    ensureGioPlugins()
    loadBusinessTracking()
    loadEnGtag()
  }

  onMounted(() => {
    // SEM 走 useHead 同步注入；首页延后 GIO/tracking；名录等非首页保持现网即时
    if (isSemPage.value) {
      scheduleIdle(loadEnGtag)
      return
    }

    if (isHomePage.value) {
      scheduleIdle(() => {
        ensureGioPlugins()
        loadBusinessTracking()
        loadDeferredTrackers()
        loadEnGtag()
      }, 2000)
    } else {
      ensureGioPlugins()
      scheduleBusinessTrackingOnLoad()
      scheduleIdle(() => {
        loadDeferredTrackers()
        loadEnGtag()
      })
    }
  })

  ensureHomeSdkRouteHook(router, ensureHomeDeferredReady)

  const headScripts = computed(() => {
    const list: any[] = []
    // isMobile：可能影响端跳转，保持原 defer，不做 idle 延后
    list.push({
      key: 'is-mobile-script',
      src: isDev()
        ? '/isMobile-test.js'
        : 'https://resources.jctrans.com/res/jc/h5/static/js/isMobile.js',
      type: 'text/javascript',
      defer: true,
      props: { 'data-ssr': false },
    })

    // SEM：巨量 / 百度 / tracking 保持原先同步挂载，避免表单转化丢点
    // gtag 由延后注入 / tracking.js，此处不再双载
    if (isSemPage.value) {
      list.push({
        key: 'douyin-script',
        type: 'text/javascript',
        innerHTML: DOUYIN_SCRIPT,
      })
      list.push({
        key: 'baidu-all-script',
        type: 'text/javascript',
        innerHTML: BAIDU_SCRIPT,
      })
      list.push({
        key: 'tracking-script',
        src: trackingSrc.value,
        type: 'text/javascript',
        defer: true,
        props: { 'data-ssr': false },
      })
    }

    return list
  })

  useHead({
    script: headScripts,
    meta: [
      {
        name: 'applicable-device',
        content: 'Mobile',
      },
    ],
  })
</script>
<style>
  body:not(.has-kuaishang) #ks_ol_floatWin,
  body:not(.has-kuaishang) #_ks_ol_inviteWin,
  body:not(.has-kuaishang) #ks_ol_chatWin {
    display: none !important;
  }
</style>
